import type { KeyboardEvent } from 'react';
import type { ReportView, UpdateReportInput } from '#shared/types';
import { describeResultRow } from './result-entry.logic';
import { ResultRow } from './ResultRow';

interface ReportResultsTableProps {
  reportName: string;
  details: UpdateReportInput['details'];
  /** The stored detail behind each row: its parameter, snapshot and ranges. */
  detailById: Map<number, ReportView['report_details'][number]>;
  expanded: number | null;
  onToggle: (detailId: number | null) => void;
  onResultChange: (detailId: number, result: string) => void;
  onRangeChange: (detailId: number, valueId: number) => void;
  makeKeyDownHandler: (index: number) => (event: KeyboardEvent) => void;
  registerRef: (index: number) => (element: HTMLInputElement | HTMLSelectElement | null) => void;
  readOnly: boolean;
}

/**
 * The result grid.
 *
 * Hand-rolled rather than driven by `DataTable`, because each row can expand
 * into a second `<tr>` and every result field needs a ref for keyboard
 * navigation. `DataTable` has no expansion slot, and adding one to serve a
 * single screen would be a feature rather than a refactor. It emits the same
 * `c-table__*` classes, so the two look identical.
 */
export function ReportResultsTable({
  reportName,
  details,
  detailById,
  expanded,
  onToggle,
  onResultChange,
  onRangeChange,
  makeKeyDownHandler,
  registerRef,
  readOnly,
}: ReportResultsTableProps) {
  return (
    <div className="c-table__scroll">
      <table className="c-table">
        <caption className="sr-only">
          Results for {reportName}. Press Enter to move to the next field.
        </caption>
        <thead>
          <tr>
            <th className="c-table__th c-entry__chevron-col">
              <span className="sr-only">Expand</span>
            </th>
            <th className="c-table__th">Parameter</th>
            <th className="c-table__th c-entry__result-col">Result</th>
            <th className="c-table__th">Unit</th>
            <th className="c-table__th">Reference range</th>
            <th className="c-table__th c-table__cell--end">Flag</th>
          </tr>
        </thead>
        <tbody>
          {details.map((detail, index) => {
            const isOpen = expanded === detail.id;
            return (
              <ResultRow
                key={detail.id}
                index={index}
                result={detail.result}
                row={describeResultRow(detail, detailById.get(detail.id))}
                readOnly={readOnly}
                isOpen={isOpen}
                onToggle={() => onToggle(isOpen ? null : detail.id)}
                onResultChange={(value) => onResultChange(detail.id, value)}
                onRangeChange={(valueId) => onRangeChange(detail.id, valueId)}
                onKeyDown={makeKeyDownHandler(index)}
                registerRef={registerRef(index)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
